import gsap from 'gsap';
import { gameModuleStore } from '../gameModule/store/gameModuleStore';
import { mousePositionToIconColumnRow } from './mousePositionToIconICoordinates';
import { gameConfigurationStore } from '../gameModule/store/gameConfigurationStore';

// RU: возврат стянутого барабана на место. 100 мс — столько же шёл прежний покадровый твин;
//     теперь это обычный твин GSAP, и он идёт по тому же тикеру, что вся игра.
const RETURN_DURATION_S = 0.1;

// RU: в реплее и интро свайпа нет: доску там ведёт ядро, а в интро у неё нет даже `doSpin`.
const isTouchControlDisabled = () => gameConfigurationStore.isReplay || gameModuleStore.isIntro;

class TouchController {
  // RU: `reelsContainer` — контейнер, в котором стоят сами барабаны. Указатель переводится в ЕГО
  //     координаты: в них заданы центры колонок и строк (MG-49813, MG-49831). Экран свайпа
  //     (`iconsContainer`) только ловит нажатия: он стоит вне слота доски, и его координаты сдвинуты
  //     на полбарабана — правая половина барабана N давала N+1.
  constructor(iconsContainer, winContainer, reels, reelsContainer) {
    this.iconsContainer = iconsContainer;
    this.winContainer = winContainer;
    this.reels = reels;
    this.reelsContainer = reelsContainer;
    this.iconsHeight = gameConfigurationStore.reelsParams[0].iconsHeight;
    this.lastY = 0;
    this.touchY = 0;
    this.touchMoving = false;
    this.moveDistance = 0;
    this.returnControl = null;
    this.pressedColumn = -1;
    this.returningToThePlace = false;
    this.activePointerId = null;
    this.target = iconsContainer;
    this.secondTarget = winContainer;
    this.setInteractive(!isTouchControlDisabled());
    this.onTouchStart = this.onTouchStart.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    // RU: отпускание и уход указателя возвращают барабан на место ВСЕГДА, даже если раунд уже
    //     не даёт им управлять. Прежде обработчиком стоял сам `returnToThePlace`, и событие
    //     указателя попадало в его `forceReturn` — «всегда» выходило случайно.
    this.onPointerRelease = () => this.returnToThePlace(true);

    iconsContainer.on('pointerdown', this.onTouchStart);
    winContainer.on('pointerdown', this.onTouchStart);
    iconsContainer.on('pointerup', this.onPointerRelease);
    iconsContainer.on('pointerupoutside', this.onPointerRelease);
    iconsContainer.on('pointermove', this.onPointerMove);
    iconsContainer.on('pointerout', this.onPointerRelease);
  }

  // RU: режим ввода — явным `eventMode`, а не legacy `interactive` (engine-gotcha №3): в v7
  //     `interactive = false` значит 'auto', а не 'none', и под корнем 'static' (им владеет Frame)
  //     пустой спрайт свайп-экрана и символы выигрыша продолжали хит-тестироваться и забирать
  //     нажатия в реплее и интро. Выключенный свайп — 'none': поддерево вне ввода целиком, в нём
  //     нет ничего, что ждёт нажатия. Корень stage здесь не пишется никогда.
  setInteractive(interactive) {
    const eventMode = interactive ? 'static' : 'none';
    this.target.eventMode = eventMode;
    this.secondTarget.eventMode = eventMode;
    this.target.cursor = interactive ? 'grab' : 'default';
    this.secondTarget.cursor = interactive ? 'grab' : 'default';
  }

  stopReturn() {
    if (this.returnControl) {
      this.returnControl.kill();
      this.returnControl = null;
    }
  }

  reset() {
    this.target.cursor = 'grab';
    this.secondTarget.cursor = 'grab';
    this.pressedColumn = -1;
    this.touchMoving = false;
    this.moveDistance = 0;
    this.activePointerId = null;
  }

  onTouchStart(event) {
    if (isTouchControlDisabled()) return;
    if (!gameModuleStore.canTouchControl && gameModuleStore.winAnimations.length) {
      gameModuleStore.doSpin();
    }

    if (this.returningToThePlace || event.data.buttons !== 1) return;
    if (this.activePointerId !== null && event.data.pointerId !== this.activePointerId) return;
    this.activePointerId = event.data.pointerId;
    const reelCoordinates = mousePositionToIconColumnRow(this.reelsContainer, event);
    if (reelCoordinates) {
      if (!this.returningToThePlace) {
        this.stopReturn();
        this.pressedColumn = reelCoordinates.column;
        this.target.cursor = 'grabbing';
        this.moveDistance = 0;
        this.touchMoving = true;
        this.lastY = event.data.global.y;
        this.touchY = event.data.global.y;
      }
    }
  }

  // RU: `forceReturn` — вернуть барабан, даже если раунд уже не даёт управлять. Без него
  //     барабан возвращается, только пока управление есть: если свайп уже запустил спин,
  //     барабан и так уезжает в прокрутку.
  returnToThePlace(forceReturn = false) {
    if (this.returningToThePlace) return;

    this.returningToThePlace = true;
    this.touchMoving = false;
    this.target.cursor = 'grab';

    const finishReturn = () => {
      this.returningToThePlace = false;
      this.reset();
    };
    if ((forceReturn || gameModuleStore.canTouchControl) && this.moveDistance !== 0) {
      const offset = { value: this.moveDistance };
      this.returnControl = gsap.to(offset, {
        value: 0,
        duration: RETURN_DURATION_S,
        ease: 'none',
        onUpdate: () => {
          if (this.pressedColumn === -1) {
            this.stopReturn();
            finishReturn();
          } else {
            this.reels[this.pressedColumn].move(offset.value - this.moveDistance);
            this.moveDistance = offset.value;
          }
        },
        onComplete: finishReturn,
      });
    } else {
      finishReturn();
    }
  }

  onPointerMove(event) {
    if (event.data.pointerId !== this.activePointerId) return;
    if (
      event.data.buttons !== 1 ||
      this.pressedColumn === -1 ||
      !this.touchMoving ||
      this.returningToThePlace ||
      !gameModuleStore.canTouchControl
    ) {
      return;
    }

    const reelCoordinates = mousePositionToIconColumnRow(this.reelsContainer, event);
    if (!reelCoordinates || reelCoordinates.column !== this.pressedColumn) {
      this.returnToThePlace();
      return;
    }

    // RU: протяжка — в единицах барабана: масштаб слота доски не совпадает с масштабом экрана свайпа.
    const reelScaleY = this.reelsContainer.worldTransform.d;
    this.moveDistance = (event.data.global.y - this.touchY) / reelScaleY;
    const passedDistance = Math.abs(this.moveDistance);
    const spinDirection = this.moveDistance / passedDistance;

    this.reels[this.pressedColumn].move(
      (event.data.global.y - this.lastY) / reelScaleY
    );
    this.lastY = event.data.global.y;

    if (passedDistance >= this.iconsHeight / 2) {
      gameModuleStore.setSpinDirection(spinDirection);
      gameModuleStore.doSpin();
      this.returnToThePlace();
    }
  }
}

export function addTouchControl(touchSpinScreen, winContainer, reels, reelsContainer) {
  new TouchController(touchSpinScreen, winContainer, reels, reelsContainer);
}
